import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Repeat, MessageSquareWarning, Database, HelpCircle, ShieldCheck, Lock, Eye } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import {
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices,
} from '@/components/site/ServicePageParts';
import { SITE } from '@/data/site';

gsap.registerPlugin(ScrollTrigger);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROBLEMS = [
  { slug: 'manual-work', icon: Repeat, title: 'Repetitive manual work', body: 'Tagging, extracting, summarising, routing. The kind of work nobody learned a degree for. AI is great at the boring 80% so your team can own the interesting 20%.' },
  { slug: 'slow-support', icon: MessageSquareWarning, title: 'Slow customer support', body: 'Median first response on email is 12 hours; on chat it should be 30 seconds. A grounded assistant answers the 60% that are obvious, escalates the rest.' },
  { slug: 'unused-data', icon: Database, title: 'Data collected but never used', body: 'Two terabytes of tickets, calls and PDFs—and nobody opens them. We turn them into the brain of an assistant that actually knows your business.' },
  { slug: 'no-direction', icon: HelpCircle, title: 'Not knowing where AI even helps', body: 'Most of the work is choosing the right problem. We run a 1-week feasibility audit before any line of code—and tell you when AI is not the answer.' },
];

const BUILD = [
  { slug: 'assistants', tag: 'Assistant · Chat', title: 'AI assistants & chatbots', body: 'Grounded chat assistants for support, sales, internal Q&A. Backed by your docs, your tone, your guardrails. Not another GPT wrapper.', accent: '#6C5CE7' },
  { slug: 'agents', tag: 'Agent · Automation', title: 'Workflow automation & agents', body: 'Multi-step agents that read, decide and act—within tight tool scopes. Triage, drafting, follow-up, reporting.', accent: '#4B9FFF' },
  { slug: 'doc-processing', tag: 'Docs · Extract', title: 'Document & data processing', body: 'Pull structured data out of PDFs, invoices, forms, contracts. Human-in-the-loop where it matters; full auto where it doesn’t.', accent: '#6C5CE7' },
  { slug: 'recommendations', tag: 'Personalisation', title: 'Recommendations & personalization', body: 'Editorial + collaborative recos for stores, content sites and SaaS. Trained on your data, owned by you.', accent: '#4B9FFF' },
  { slug: 'predictive', tag: 'ML · Predictive', title: 'Predictive analytics', body: 'Churn, propensity, demand—built as predictions inside the products and dashboards your team already opens.', accent: '#4B9FFF' },
  { slug: 'features', tag: 'Embedded · SDK', title: 'AI features inside existing products', body: 'Summarise, rewrite, classify, search—dropped into the screens your users already use. Latency-budgeted, eval-gated, cost-capped.', accent: '#6C5CE7' },
  { slug: 'kb-rag', tag: 'RAG · Knowledge', title: 'Assistants grounded in your knowledge base', body: 'RAG done seriously: chunking strategy, hybrid search, citation-first answers, refusal-when-unsure behaviour, evals.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'discovery', step: '01', title: 'Use-case discovery & feasibility', body: 'A one-week audit. We map the top five candidates, score them on impact and feasibility, and tell you—honestly—whether AI is the right tool for each.' },
  { slug: 'data-readiness', step: '02', title: 'Data readiness', body: 'Inventory your sources, audit quality, decide on chunking + embedding strategy. The 80% of an AI project that nobody films a demo of.' },
  { slug: 'prototype', step: '03', title: 'Prototype / POC', body: 'A scoped POC in two to four weeks. Live, evaluable, not a slide deck. Real users on it before week five.' },
  { slug: 'integration', step: '04', title: 'Integration', body: 'Into your product surface, your CRM, your ops console. We do not believe in standalone "AI portals" unless that is literally the product.' },
  { slug: 'evaluation', step: '05', title: 'Evaluation & guardrails', body: 'Eval suites with ground-truth examples, refusal tests, jailbreak tests, latency + cost budgets. Quality is a number, not a vibe.' },
  { slug: 'deployment', step: '06', title: 'Deployment', body: 'A staged rollout: shadow → 5% → 25% → 100%. Kill-switches, fallback to deterministic logic, on-call playbook.' },
  { slug: 'monitoring', step: '07', title: 'Monitoring & improvement', body: 'Observability for prompts, citations, costs, refusals. A monthly review cadence with model + prompt changes shipped behind feature flags.' },
];

const FAQ = [
  { q: 'How do you keep our data safe?', a: 'Two layers. First: the assistant only sees the documents you gate it to—nothing else. Second: we use providers with no-training clauses, encrypt at rest, and offer self-hosted embeddings or open-source models when policy demands it.' },
  { q: 'Will it hallucinate?', a: 'Less than you think, if it is built right. We use citation-first prompts, retrieval-grounded answers, refusal heuristics, and an eval suite that catches regressions before deploy.' },
  { q: 'Can it run on our own infrastructure?', a: 'Yes. We deploy to your AWS / GCP / Azure account. For sensitive workloads we run open-source models (Llama, Mistral, Qwen) on your own GPUs.' },
  { q: 'How much will it cost to run?', a: 'We set a per-conversation cost ceiling in the design phase and stay under it. For a grounded support assistant, ₹0.40–₹2.00 per resolved query is typical at Indian volumes.' },
  { q: 'Which models do you use?', a: 'Whichever one matches the job. Claude Sonnet / Opus for reasoning-heavy work, GPT-5 family for tool use + structured output, Gemini for very large context, open models when policy or cost demands it.' },
  { q: 'How long to ship a v1?', a: 'A scoped POC is 2–4 weeks. A production-grade assistant with evals, guardrails and monitoring is 8–12 weeks.' },
];

const OUTCOMES = [
  { kpi: 64, suffix: '%', label: 'queries auto-resolved (median)' },
  { kpi: 3800, suffix: '+', label: 'support hrs saved / yr / team' },
  { kpi: 92, suffix: '%', label: 'eval accuracy on ground truth' },
  { kpi: 1.4, suffix: 's', label: 'p50 first token (RAG)' },
];

const RESP_AI = [
  { icon: ShieldCheck, k: 'No training on your data', v: 'We only use vendors with no-training contractual clauses. Your data is yours. Period.' },
  { icon: Lock, k: 'PII redaction in-flight', v: 'Names, phone numbers, IDs and free-text are scanned before they ever reach a model. Configurable per field.' },
  { icon: Eye, k: 'Citations or it didn’t happen', v: 'Every grounded answer carries citations to the source chunks. Users can audit. Reviewers can verify.' },
];

const SUGGESTIONS = [
  'What does NioDelta actually do?',
  'How long does a v1 AI assistant take?',
  'Can you work with our existing tech stack?',
  'How is this different from buying ChatGPT Enterprise?',
];

/* ----- Signature: live mini-assistant ----- */
function MiniAssistant() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi — I'm NioBot, a grounded mini-assistant. Ask me anything about how NioDelta works." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    if (busy) return;
    const t = (text ?? input).trim();
    if (!t) return;
    setInput('');
    const history = messages.slice(-6).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: t }, { role: 'assistant', content: '' }]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: t, session_id: sessionId, history }),
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        setMessages((prev) => {
          const next = prev.slice();
          next[next.length - 1] = { role: 'assistant', content: buf };
          return next;
        });
      }
      if (!sessionId) setSessionId(`niobot-${Date.now()}`);
    } catch (err) {
      setMessages((prev) => {
        const next = prev.slice();
        next[next.length - 1] = {
          role: 'assistant',
          content: `Sorry — the assistant is temporarily offline. WhatsApp a partner instead: ${SITE.whatsapp}`,
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ai-assistant"
      className="rounded-[20px] border border-white/[0.12] bg-ink-surface overflow-hidden flex flex-col h-[560px]"
    >
      {/* header */}
      <div className="px-5 py-4 border-b border-white/[0.08] flex items-center justify-between bg-black/30">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-violet/15 border border-violet/30 flex items-center justify-center">
            <svg viewBox="0 0 32 32" className="h-4 w-4"><path d="M16 6 L26 24 L6 24 Z" fill="none" stroke="#6C5CE7" strokeWidth="2"/></svg>
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">NioBot</div>
            <div className="text-[11px] text-ink-muted">Grounded in NioDelta&apos;s services · live demo</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-ink-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          online
        </div>
      </div>

      {/* messages */}
      <div ref={scrollRef} data-testid="ai-messages" className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-violet text-white rounded-br-md'
                  : 'bg-white/[0.05] border border-white/[0.10] text-ink-text rounded-bl-md'
              }`}
            >
              {m.content || (m.role === 'assistant' && busy && i === messages.length - 1
                ? <span className="inline-flex gap-1 items-center text-ink-muted">
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" />
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                : null)}
            </div>
          </div>
        ))}
      </div>

      {/* suggestions */}
      {messages.length <= 1 && (
        <div className="px-5 pb-2 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              type="button"
              data-testid={`ai-suggest-${i}`}
              onClick={() => send(s)}
              className="text-[12px] px-3 py-1.5 rounded-full border border-white/[0.12] text-white/85 hover:border-white/30 hover:bg-white/[0.04] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="px-4 py-4 border-t border-white/[0.08] bg-black/30 flex items-center gap-2"
      >
        <input
          data-testid="ai-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask NioBot anything about our services…"
          disabled={busy}
          className="input flex-1"
        />
        <button
          type="submit"
          data-testid="ai-send"
          disabled={busy || !input.trim()}
          className="inline-flex items-center gap-2 rounded-full bg-violet text-white px-4 py-2.5 text-sm hover:shadow-[0_10px_30px_-10px_rgba(108,92,231,0.6)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Thinking…' : 'Send'} →
        </button>
      </form>
    </div>
  );
}

export default function AISolutions() {
  return (
    <div data-testid="ai-solutions-page">
      <ServiceHero
        serviceNum="06"
        serviceTitle="AI Solutions"
        headlineText="AI that does real work for your business."
        accent="work"
        description="RAG, agents, document processing and AI features inside existing products. Built with evals, guardrails and a cost ceiling—not a vibe. The kind of AI you can sign a contract against."
        secondaryCta={{ label: 'Try the assistant below', to: '#ai-try' }}
        testIdPrefix="ai"
      />

      <ProblemsBlock
        heading="Four shapes of AI-shaped pain."
        accentWord="AI-shaped pain."
        intro="If one of these sounds familiar, AI probably has a contribution to make. If none of them do, we&apos;ll tell you that too—on the first call."
        items={PROBLEMS}
        testIdPrefix="ai"
      />

      <BuildBlock
        heading="Seven shapes of AI we put into production."
        accentWord="production."
        intro="Not every problem is a chatbot. We pick the shape that actually moves the metric."
        items={BUILD}
        testIdPrefix="ai"
      />

      <ApproachBlock
        heading="Seven stages. One eval suite."
        accentWord="eval suite."
        intro="Quality is a number, not a vibe. Every step of our approach is anchored to a metric you can read in a board meeting."
        steps={APPROACH}
        testIdPrefix="ai"
      />

      {/* Signature: try the assistant */}
      <section id="ai-try" data-testid="ai-try" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="05">Try it · Live mini-assistant</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                Ask NioBot a question. <span className="italic text-violet">Streaming response.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  A grounded mini-assistant wired to <code className="text-violet">/api/ai/chat/stream</code> on this site. It only answers about NioDelta&apos;s services—and refuses what it doesn&apos;t know.
                </p>
              </Reveal>
            </div>
          </div>
          <Reveal>
            <MiniAssistant />
          </Reveal>
        </div>
      </section>

      {/* Responsible AI */}
      <section data-testid="ai-responsible" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="06">Responsible AI</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              The boring slide we made <span className="italic text-electric">interesting.</span>
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">
              Trust is the conversion lever for AI work. We design for it on day one—not as a launch checklist.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {RESP_AI.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.k} delay={i * 80}>
                  <div data-testid={`ai-resp-${i}`} className="h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6">
                    <div className="h-10 w-10 rounded-xl border border-white/[0.12] flex items-center justify-center text-electric bg-white/[0.02]">
                      <Icon size={18} />
                    </div>
                    <div className="mt-5 text-lg font-semibold tracking-tight">{r.k}</div>
                    <p className="mt-3 text-ink-muted leading-relaxed text-sm">{r.v}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case study mini */}
      <section data-testid="ai-case" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionLabel num="07">Case study</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              <span className="italic text-violet">Vedaa AI</span> · A grounded copilot for Ayurvedic practitioners.
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-md">
              1,400 pages of formulary, 240 case files, six language variants. Citation-first answers with refusal when a query falls outside the corpus. 92% accuracy on the gold eval set.
            </p>
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ai-case-cta"
              className="arrow-link mt-8 inline-flex items-center gap-2 text-sm text-white"
            >
              Discuss a similar build <span className="arrow">→</span>
            </a>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4 self-start">
            {[
              { k: '92%', l: 'accuracy on eval set' },
              { k: '1,400', l: 'pages indexed (RAG)' },
              { k: '< 1.5s', l: 'p50 first token' },
              { k: '0', l: 'hallucinations on refusal set' },
            ].map((it) => (
              <div key={it.l} className="rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
                <div className="text-3xl md:text-4xl font-bold tracking-tight tabular-nums text-white">{it.k}</div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-ink-muted">{it.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OutcomesBlock
        heading="Numbers we’d sign a contract against."
        accentWord="contract"
        intro="Medians across the last 7 AI engagements (2024–25). The boring metrics, on purpose—latency, accuracy, savings."
        values={OUTCOMES}
        testIdPrefix="ai"
      />

      <section data-testid="ai-faq" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="09">FAQ</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Things we&apos;re asked <span className="italic text-violet">before</span> the call.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQList items={FAQ} testIdPrefix="ai-faq" />
          </div>
        </div>
      </section>

      <RelatedServices exclude="ai-solutions" testIdPrefix="ai" />

      <CTABand
        eyebrow="Have a use-case in mind?"
        headline="Tell us what AI is supposed to do."
        sub="A 30-minute WhatsApp call with a partner. We&apos;ll be honest about whether AI is the right tool for your problem."
      />
    </div>
  );
}
